import { Component, ElementRef, inject, OnInit, AfterViewInit, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  LoadingSpinnerComponent,
} from '@verticalbridge/ui-components';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { isAllowedExternalRedirect } from '../../core/auth/external-redirect';
import { RequestAccessService } from '../../core/services/request-access.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { AuthLayoutComponent } from '../../shared/components/auth-layout/auth-layout.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    AuthLayoutComponent,
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, AfterViewInit {
  private authService = inject(PortalAuthService);
  private requestAccessService = inject(RequestAccessService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private el = inject(ElementRef);

  email = '';
  password = '';
  isRememberMe = false;
  isLoading = false;
  isAuthenticating = false;
  errorMessage = '';
  currentYear = new Date().getFullYear();

  ngOnInit() {
    // If ?logout=true, immediately logout and clear session
    if (this.route.snapshot.queryParamMap.get('logout') === 'true') {
      this.authService.logout();
      return;
    }

    // Capture external redirect query params from any caller (Partner
    // Portal uses `redirectUrl`; Online Colo uses `returnUrl` + `source`).
    //
    // SECURITY: the same allowlist AppComponent's constructor applies. This
    // path stores the key itself rather than going through AppComponent, so
    // without the gate here a crafted `/?returnUrl=https://evil/&source=colo`
    // reached `handlePostLoginRedirect`, which appends the fresh access token
    // and navigates with no origin check of its own.
    const queryParams = this.route.snapshot.queryParamMap;
    const externalRedirect =
      queryParams.get('returnUrl') ?? queryParams.get('redirectUrl');
    if (externalRedirect && isAllowedExternalRedirect(externalRedirect)) {
      localStorage.setItem('externalRedirectUrl', externalRedirect);
    } else if (externalRedirect) {
      console.warn(
        'LoginComponent: dropping external redirect to non-allowlisted origin',
        externalRedirect
      );
    }
    // The source carries no token, so it is stored either way: the auth
    // layout uses it only to name the app the user is signing in to.
    const externalSource = queryParams.get('source');
    if (externalSource) {
      localStorage.setItem('externalRedirectSource', externalSource);
    }

    // Seamless handoff: when an external app (VI) hands us off (source+returnUrl)
    // and we have no session, redirect a VB user straight to Azure AD via the
    // enterprise connection (no prompt=none) so AAD SSO signs them in with no
    // visible login. prompt=none is deliberately NOT used: it requires an
    // existing Auth0 session, cannot bootstrap one from the upstream AAD session,
    // and its login_required callback is swallowed by the Auth0 SDK before we can
    // react to it. Non-VB users (external VIntel / Colo, no AAD account) fall
    // through to the login form. Guarded to fire at most once per handoff.
    const search = new URLSearchParams(window.location.search);
    if (search.get('error')) {
      // The federated redirect came back with an error (e.g. the user cancelled
      // Microsoft sign-in). Strip the params and render the form; the one-shot
      // guard below prevents an immediate re-attempt.
      const clean = new URL(window.location.href);
      clean.searchParams.delete('error');
      clean.searchParams.delete('error_description');
      clean.searchParams.delete('state');
      window.history.replaceState({}, '', clean.pathname + clean.search + clean.hash);
    } else if (search.has('code') && search.has('state')) {
      // Auth0 callback in flight — AppComponent is exchanging the code and the
      // post-login redirect will navigate away. Show "Signing you in…" instead
      // of the form so the handoff doesn't look like a login prompt.
      this.beginAuthenticatingState();
    } else if (
      sessionStorage.getItem('portalHandoffPending') &&
      !this.authService.getToken() &&
      !sessionStorage.getItem('portalHandoffAttempted')
    ) {
      const loginHint = localStorage.getItem('externalLoginHint');
      if (this.isVerticalBridgeEmail(loginHint ?? '')) {
        sessionStorage.setItem('portalHandoffAttempted', 'true');
        localStorage.removeItem('externalLoginHint');
        this.beginAuthenticatingState();
        this.authService.federatedLoginForHandoff(loginHint);
        return;
      }
    }

    // Redirect if already authenticated (e.g. user navigates to / while logged in).
    // Skip during active login — the loginDirect subscriber handles that navigation.
    this.authService.isAuthenticated().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(isAuth => {
      if (isAuth && !this.isLoading) {
        if (!this.authService.handlePostLoginRedirect()) {
          this.router.navigate(['/home']);
        }
      }
    });
  }

  ngAfterViewInit() {
    queueMicrotask(() => {
      const emailInput = this.el.nativeElement.querySelector('input[type="email"]') as HTMLInputElement | null;
      emailInput?.focus();
    });
  }

  loginDirect() {
    if (this.isVerticalBridgeEmail(this.email)) {
      this.errorMessage = 'Vertical Bridge accounts must sign in with Microsoft. Please use the Microsoft option below.';
      return;
    }

    if (!this.email || !this.password) {
      this.errorMessage = 'Please enter your email and password';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.authService.loginDirect(this.email, this.password).pipe(
      // After login, run the shared Colo provisioning chain. Catches
      // returning users whose registration was interrupted before their
      // OC user record was created.
      switchMap(() =>
        this.requestAccessService.ensureColoUserProvisioned(this.email)
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        // Redirect non-VB users with PP access directly to Partner Portal
        if (this.authService.handlePostLoginRedirect()) return;

        // Check for internal redirect URL
        const redirectUrl = localStorage.getItem('redirectUrl');
        if (redirectUrl) {
          localStorage.removeItem('redirectUrl');
          this.router.navigateByUrl(redirectUrl);
        } else {
          this.router.navigate(['/home']);
        }
      },
      error: (error) => {
        this.isLoading = false;

        if (error.status === 403 || error.status === 401) {
          this.errorMessage = 'Invalid email or password';
        } else if (error.status === 429) {
          this.errorMessage = 'Too many login attempts. Please try again later';
        } else {
          this.errorMessage = 'Login failed. Please try again';
        }
      }
    });
  }

  loginWithSocial(provider: string) {
    this.isLoading = true;
    this.authService.loginWithProvider(provider);
  }

  private isVerticalBridgeEmail(email: string): boolean {
    return email.trim().toLowerCase().endsWith('@verticalbridge.com');
  }

  // Safety valve: if authentication never completes (e.g. the code exchange
  // silently fails and nothing navigates), fall back to the login form rather
  // than leaving the user on an endless spinner.
  private beginAuthenticatingState(): void {
    this.isAuthenticating = true;
    setTimeout(() => {
      this.isAuthenticating = false;
    }, 15_000);
  }
}

